import React from "react";
import { motion } from "framer-motion";
import { Phone } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL_HREF } from "@/lib/contact";
import { track } from "@/lib/analytics";
import { fadeUp, fadeUpStagger, IN_VIEW, EASE_OUT_EXPO } from "@/lib/motion";

/**
 * Manifesto: five numbered chapters that describe why we exist.
 * Editorial layout, kinetic on scroll, deliberately slow — the "essay" moment.
 */
const CHAPTERS = [
  {
    n: "01",
    title: "Ogni minuto conta.",
    body:
      "Un guasto non chiede permesso. Rispondiamo in pochi secondi e ci muoviamo dal punto più vicino a te: tempi d'intervento ridotti al minimo, anche nel traffico romano.",
  },
  {
    n: "02",
    title: "Sempre. Davvero.",
    body:
      "24 ore su 24, 7 giorni su 7. Notti, weekend, Ferragosto, Natale. La nostra centrale non chiude mai, e nemmeno i nostri mezzi.",
  },
  {
    n: "03",
    title: "Persone, non centralini.",
    body:
      "Al telefono risponde chi conosce Roma metro per metro. Ti guidiamo, ti tranquillizziamo, poi arriviamo. Nessun disco registrato, nessuna attesa infinita.",
  },
  {
    n: "04",
    title: "Trasparenza, sul posto.",
    body:
      "Preventivo chiaro prima della partenza. Contanti o carta direttamente al carro attrezzi. Niente sorprese, niente sovrapprezzi in strada.",
  },
  {
    n: "05",
    title: "Ovunque a Roma.",
    body:
      "Centro storico, EUR, Ostia, Fiumicino, provincia intera. Auto ribaltate, infossate, incastrate nella sabbia, ferme per blocco della centralina: siamo attrezzati per i recuperi complessi.",
  },
];

export default function Manifesto() {
  return (
    <section
      id="manifesto"
      data-testid="manifesto-section"
      className="relative py-24 md:py-36 bg-navy text-white overflow-hidden grain"
    >
      <div className="absolute inset-0 hero-grid opacity-25 pointer-events-none" />
      <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-brand-orange/15 blur-[140px] pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <motion.header
          initial="hidden"
          whileInView="visible"
          viewport={IN_VIEW}
          variants={fadeUpStagger}
          className="max-w-3xl"
        >
          <motion.p
            variants={fadeUp}
            className="text-brand-orange text-xs sm:text-sm font-bold tracking-[0.35em] uppercase"
          >
            — Manifesto
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="mt-5 font-display font-black text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight"
          >
            Non un servizio.{" "}
            <span className="text-brand-orange italic">Una promessa</span>{" "}
            in strada.
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="mt-6 text-white/70 text-base sm:text-lg leading-relaxed max-w-2xl"
          >
            Cinque principi, non negoziabili. Sono la ragione per cui, a Roma,
            ci chiamano prima di chiunque altro.
          </motion.p>
        </motion.header>

        <ol className="mt-16 md:mt-24 divide-y divide-white/10 border-t border-white/10">
          {CHAPTERS.map((c, i) => (
            <motion.li
              key={c.n}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={IN_VIEW}
              transition={{ duration: 0.85, ease: EASE_OUT_EXPO, delay: 0.02 * i }}
              data-testid={`manifesto-chapter-${i}`}
              className="group relative grid grid-cols-12 gap-4 md:gap-8 py-8 md:py-12"
            >
              <div className="col-span-2 md:col-span-2">
                <span className="font-display font-black text-brand-orange text-3xl sm:text-5xl lg:text-6xl leading-none tracking-tight">
                  {c.n}
                </span>
              </div>
              <div className="col-span-10 md:col-span-6">
                <h3 className="font-display font-black text-2xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                  {c.title}
                </h3>
              </div>
              <div className="col-span-12 md:col-span-4">
                <p className="text-white/70 text-base sm:text-lg leading-relaxed">
                  {c.body}
                </p>
              </div>
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-brand-orange/60 to-transparent opacity-0 group-hover:opacity-100 transition"
              />
            </motion.li>
          ))}
        </ol>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={IN_VIEW}
          transition={{ duration: 0.9, ease: EASE_OUT_EXPO }}
          className="mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <a
            href={PHONE_TEL_HREF}
            onClick={() => track.phoneCall("manifesto")}
            data-testid="manifesto-call-button"
            className="inline-flex items-center gap-3 rounded-2xl bg-brand-orange text-white px-6 py-4 font-bold text-base sm:text-lg hover:-translate-y-0.5 hover:brightness-110 transition btn-glow-orange"
          >
            <Phone className="h-5 w-5" />
            Chiama {PHONE_DISPLAY}
          </a>
          <span className="text-white/50 text-sm font-mono tracking-widest uppercase">
            — Risposta immediata, tutta Roma
          </span>
        </motion.div>
      </div>
    </section>
  );
}
